import {find, map, transform} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, PieChart, Tooltip} from 'components';
import {SessionHistoryStatistic} from 'interfaces';
import {getPercentage} from 'utils/transform';

import {ActivityMetricDefaultColor} from '../../enums';
import {ActivityMetricConfig} from '../../types';

type Props = {
    title: string;
    statistic: SessionHistoryStatistic;
    configs: ActivityMetricConfig[];
};

export const ActivityMetricsPieChart: React.FC<Props> = ({title, statistic, configs}: Props) => {
    const {t} = useTranslation();
    const [chartData, setChartData] = useState([]);

    const metrics = useMemo(() => map(configs, (config) => config.metric), [configs]);
    const defaultValue = useMemo(() => transform(configs, (acc, config) => acc.push({name: config.metric, value: 0}), []), [configs]);
    const total = useMemo(() => configs?.[0]?.totalGetter?.(statistic), [configs, statistic]);

    const getMetricsChartData = useCallback(() =>
        !statistic ? defaultValue : transform(configs, (acc, config) => {
            if (!metrics.includes(config.metric)) {
                return;
            }
            acc.push({name: config.metric, value: config.valueGetter(statistic) || find(defaultValue, {name: config.metric})?.value});
        }, []),
    [statistic, defaultValue, configs, metrics]);

    const tooltipMessage = useMemo(() => (
        map(configs, (config) => (
            <div key={config.metric} className='tw-ts-base tw-mb-2'>
                <span className='tw-ts-base-bold'>
                    {t(config.title)}
                </span>
                <span>{' - '}</span>
                {t(config.tooltip)}
            </div>
        ))
    ), [t, configs]);

    useEffect(() => {
        setChartData(getMetricsChartData());
    }, [statistic, getMetricsChartData]);

    const [cells, setCells] = useState([]);

    useEffect(() => {
        const cellsConfig = configs.map(({metric, color, ratioColor}) => ({
            stroke: ratioColor || color,
            fill: ratioColor || color,
            name: metric,
        }));
        setCells(cellsConfig);
    }, [t, configs, metrics]);

    return (
        <div className='tw-ts-h5 tw-mb-6'>
            <div className='tw-text-center tw-flex-center'>
                <span className='tw-ts-h3'>{title}</span>
                <Tooltip header={title} message={tooltipMessage}>
                    <Icon name='question' size='xs' className='tw-ml-2' />
                </Tooltip>
            </div>
            <div className='tw-h-72'>
                <PieChart
                    innerRadius={65}
                    outerRadius={130}
                    startAngle={90}
                    endAngle={-270}
                    margin={{top: 20, right: 20, left: -10, bottom: 5}}
                    noDataCell={{fill: '#fff', stroke: ActivityMetricDefaultColor}}
                    cells={cells}
                    data={chartData}
                    innerLabel={total}
                    innerLabelClass='tw-font-bold'
                />
            </div>
            <div className='tw-table tw-mx-auto'>
                {map(cells, (cell) => {
                    const metric = cell.name;
                    const value = find(chartData, {name: metric})?.value;
                    const config = find(configs, (c) => c.metric === metric);
                    return (
                        <div key={cell.name} className='tw-table-row'>
                            <div className='tw-table-cell tw-pr-3 tw-pb-1'>
                                <div className='tw-flex tw-items-center'>
                                    <div
                                        className='tw-w-4 tw-h-4 tw-inline-block tw-rounded-full tw-mr-2'
                                        style={{backgroundColor: cell.fill}}
                                    />
                                    {t(config.title)}
                                </div>
                            </div>
                            <div className='tw-table-cell tw-pr-3'>{value}</div>
                            <div className='tw-table-cell'>{`(${getPercentage(value, total)}%)`}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
